import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import MapIcon from '@mui/icons-material/Map';
import { ACCENTS } from '../lib/theme';

/**
 * A link that opens the phone's own maps app on an address.
 *
 * Apple Maps on anything Apple — iPhone, iPad and Mac — and Google Maps on
 * everything else. Both are ordinary https links that the device hands to the
 * installed app, so there is no app-specific scheme to break when an app is
 * missing — the worst case is the map opening in the browser instead. (iPadOS
 * reports itself as a Mac, which this now covers without a special case.)
 *
 * ", Australia" is appended because addresses are stored the way the office
 * types them ("12 Main Rd Eltham"), and without a country the search can land
 * on a street of the same name overseas.
 */
export function mapsUrl(address: string): string {
    const query = /australia/i.test(address) ? address : `${address}, Australia`;
    const ua = navigator.userAgent;
    const isApple = /iPhone|iPad|iPod|Macintosh/.test(ua);
    return isApple
        ? `https://maps.apple.com/?q=${encodeURIComponent(query)}`
        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/**
 * The map icon in a booking card's top-right corner, labelled "Map" so nobody
 * has to guess what it does. Renders nothing without an address.
 */
export function MapLink({ address }: { address: string | null }): React.JSX.Element | null {
    const trimmed = address?.trim() ?? '';
    if (trimmed === '') {
        return null;
    }
    return (
        <Box
            component="a"
            href={mapsUrl(trimmed)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${trimmed} in Maps`}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flexShrink: 0,
                mt: -0.5,
                mr: -0.5,
                px: 0.75,
                py: 0.5,
                borderRadius: 1.5,
                color: ACCENTS.blue,
                textDecoration: 'none',
                '&:hover, &:active': { bgcolor: alpha(ACCENTS.blue, 0.12) },
            }}
        >
            <MapIcon sx={{ fontSize: 34 }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1, mt: 0.25 }}>Map</Typography>
        </Box>
    );
}
